import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ViList from "@/components/vi/ViList";
import { StatusBadge } from "@/components/StatusBadge";
import { PageHeader, Loader } from "@/components/ui-kit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Pick } from "@/components/vi/SaleDialog";
import { Card, MiniTable } from "@/components/vi/TourParts";
import { Truck } from "lucide-react";
import { call, money, VI_MANAGERS } from "@/lib/vi";

const dt = (s) => (s || "").slice(0, 16).replace("T", " ");
const ORDER_STEPS = ["Commandée", "À préparer", "Préparée", "À livrer", "En livraison", "Annulée"];

function DeliverDialog({ order, onClose, onDone }) {
  const [qty, setQty] = useState({});
  const [livreur, setLivreur] = useState("");
  useEffect(() => { if (order) setQty(Object.fromEntries(order.lines.map((l) => [l.article_id, l.quantite - (l.quantite_livree || 0)]))); }, [order]);
  if (!order) return null;
  const submit = async () => {
    const r = await call(() => api.post(`/itinerant/orders/${order.id}/deliver`, { livreur, lines: Object.entries(qty).map(([article_id, quantite]) => ({ article_id, quantite: Number(quantite) })) }), "Livraison enregistrée, facture créée");
    if (r) { onClose(); onDone(); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="deliver-dialog">
        <DialogHeader><DialogTitle className="font-head">Livrer {order.numero} — {order.client_name}</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">Le stock sort de l'entrepôt {order.entrepot_name}. Une facture et un bon de livraison sont créés.</p>
        {order.lines.map((l) => (
          <div key={l.article_id} className="flex items-center gap-2 text-sm">
            <span className="flex-1">{l.designation} <span className="text-xs text-slate-400">(commandé {l.quantite}, livré {l.quantite_livree || 0})</span></span>
            <Input type="number" className="w-24 h-8" value={qty[l.article_id] ?? 0} onChange={(e) => setQty({ ...qty, [l.article_id]: e.target.value })} data-testid={`deliver-qty-${l.reference}`} />
          </div>
        ))}
        <Input placeholder="Livreur" value={livreur} onChange={(e) => setLivreur(e.target.value)} />
        <DialogFooter><Button variant="outline" onClick={onClose}>Annuler</Button><Button onClick={submit} className="bg-brand-600 text-white" data-testid="deliver-submit">Livrer</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ViOrders() {
  const { role } = useAuth();
  const isManager = VI_MANAGERS.includes(role);
  const [key, setKey] = useState(0);
  const [deliver, setDeliver] = useState(null);
  const setStatus = async (o, statut) => {
    const motif = statut === "Annulée" ? window.prompt("Motif d'annulation") : "";
    if (statut === "Annulée" && !motif) return;
    if (await call(() => api.patch(`/itinerant/orders/${o.id}/status`, { statut, motif }), `Commande : ${statut}`)) setKey((k) => k + 1);
  };
  const open = (o) => !["Livrée", "Annulée"].includes(o.statut);
  return (
    <>
      <ViList title="Commandes terrain" subtitle="Commandes entrepôt prises en tournée — livrées depuis l'entrepôt de rattachement" endpoint="/itinerant/orders" testid="vi-orders" reloadKey={key}
        columns={[{ key: "numero", label: "N°" }, { key: "created_at", label: "Date", render: (o) => dt(o.created_at) }, { key: "tournee_numero", label: "Tournée" },
          { key: "vendeur_name", label: "Vendeur" }, { key: "client_name", label: "Client" }, { key: "l", label: "Articles", render: (o) => o.lines.map((l) => `${l.designation} ×${l.quantite}`).join(", ") },
          { key: "montant", label: "Montant", render: (o) => money(o.montant) }, { key: "date_souhaitee", label: "Souhaitée" }, { key: "urgence", label: "Urgence" },
          { key: "statut", label: "Statut", render: (o) => <StatusBadge value={o.statut} /> },
          { key: "f", label: "Factures", render: (o) => (o.deliveries || []).map((d) => d.invoice_numero).join(", ") },
          { key: "a", label: "", render: (o) => isManager && open(o) && <div className="flex gap-1 items-center">
            <div className="w-36"><Pick value={o.statut} onChange={(v) => setStatus(o, v)} testid={`order-status-${o.numero}`} options={ORDER_STEPS.map((s) => ({ value: s, label: s }))} /></div>
            <Button size="sm" className="bg-brand-600 text-white h-8" onClick={() => setDeliver(o)} data-testid={`order-deliver-${o.numero}`}><Truck className="h-4 w-4 mr-1" />Livrer</Button></div> }]} />
      <DeliverDialog order={deliver} onClose={() => setDeliver(null)} onDone={() => setKey((k) => k + 1)} />
    </>
  );
}

export const ViVisits = () => <ViList title="Visites" subtitle="Check-in / check-out des boutiques" endpoint="/itinerant/visits" testid="vi-visits"
  columns={[{ key: "numero", label: "N°" }, { key: "tournee_numero", label: "Tournée" }, { key: "vendeur_name", label: "Vendeur" }, { key: "cible", label: "Boutique" },
    { key: "type", label: "Type" }, { key: "checkin_at", label: "Check-in", render: (v) => dt(v.checkin_at) }, { key: "checkout_at", label: "Check-out", render: (v) => dt(v.checkout_at) || "en cours" },
    { key: "duree_min", label: "Durée", render: (v) => (v.duree_min != null ? `${v.duree_min} min` : "—") }, { key: "resultat", label: "Résultat" },
    { key: "position", label: "GPS", render: (v) => (v.position ? <a className="text-brand-700 hover:underline" target="_blank" rel="noreferrer" href={`https://maps.google.com/?q=${v.position.lat},${v.position.lng}`}>Carte</a> : "—") }]} />;

export function ViQuotes() {
  const [key, setKey] = useState(0);
  const act = async (q, path, msg) => { if (await call(() => api.post(`/itinerant/quotes/${q.id}/${path}`), msg)) setKey((k) => k + 1); };
  return <ViList title="Devis terrain" subtitle="Devis du module Devis créés en tournée (envoi et PDF depuis le module Devis)" endpoint="/itinerant/quotes" testid="vi-quotes" reloadKey={key}
    actions={<Link to="/devis"><Button variant="outline">Module Devis</Button></Link>}
    columns={[{ key: "numero", label: "N°" }, { key: "date", label: "Date" }, { key: "owner_name", label: "Vendeur" }, { key: "client_name", label: "Client" },
      { key: "total_ttc", label: "Montant", render: (q) => money(q.total_ttc) }, { key: "statut", label: "Statut", render: (q) => <StatusBadge value={q.statut} /> },
      { key: "a", label: "", render: (q) => !q.field_order_id && !["Annulé", "Refusé"].includes(q.statut) && <div className="flex gap-1">
        <Button size="sm" variant="outline" className="h-8" onClick={() => act(q, "to-order", "Commande créée")} data-testid={`quote-to-order-${q.numero}`}>→ Commande</Button>
        <Button size="sm" variant="ghost" className="h-8 text-rose-600" onClick={() => act(q, "cancel", "Devis annulé")} data-testid={`quote-cancel-${q.numero}`}>Annuler</Button></div> }]} />;
}

const KPIS = [["ca", "CA"], ["visites", "Visites"], ["ventes", "Ventes"], ["nouveaux", "Nouveaux clients"], ["commandes", "Commandes"]];
const PERIODS = [["jour", "Aujourd'hui"], ["semaine", "Cette semaine"], ["mois", "Ce mois"]];

function ObjectiveEditor({ vendor, periode, current, onSaved }) {
  const [v, setV] = useState(Object.fromEntries(KPIS.map(([k]) => [k, current?.[k]?.objectif || 0])));
  const save = async () => { if (await call(() => api.put("/itinerant/objectives", { vendeur_id: vendor, periode, ...v }), "Objectifs enregistrés")) onSaved(); };
  return (
    <div className="flex flex-wrap gap-2 items-end mt-2">
      {KPIS.map(([k, l]) => <label key={k} className="text-[10px] uppercase font-mono text-slate-400">{l}<Input type="number" className="h-8 w-28" value={v[k]} onChange={(e) => setV({ ...v, [k]: Number(e.target.value) })} data-testid={`obj-${periode}-${k}`} /></label>)}
      <Button size="sm" className="h-8 bg-brand-600 text-white" onClick={save} data-testid={`obj-save-${periode}`}>Enregistrer</Button>
    </div>
  );
}

export function ViObjectives() {
  const { role } = useAuth();
  const isManager = VI_MANAGERS.includes(role);
  const [data, setData] = useState(null);
  const load = () => api.get("/itinerant/objectives/progress").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);
  if (!data) return <Loader />;
  return (
    <div data-testid="vi-objectives">
      <PageHeader title="Objectifs" subtitle="Réalisé / objectif / % par jour, semaine et mois" />
      {data.length === 0 && <p className="text-sm text-slate-400">Aucun conducteur-vendeur.</p>}
      {data.map((d) => (
        <Card key={d.vendeur_id} title={d.vendeur_name} testid={`obj-card-${d.vendeur_id}`}>
          {PERIODS.map(([p, l]) => (
            <div key={p} className="mb-4">
              <p className="text-xs font-semibold mb-1">{l}</p>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                {KPIS.map(([k, kl]) => { const x = d.periodes[p][k]; return (
                  <div key={k} className="rounded-lg border p-2" data-testid={`obj-${d.vendeur_id}-${p}-${k}`}>
                    <p className="text-[10px] uppercase font-mono text-slate-400">{kl}</p>
                    <p className="text-sm font-mono">{k === "ca" ? money(x.realise) : x.realise} / {k === "ca" ? money(x.objectif) : x.objectif}</p>
                    <div className="h-1.5 rounded-full bg-slate-100 mt-1"><div className={`h-1.5 rounded-full ${x.pct >= 100 ? "bg-emerald-500" : "bg-brand-600"}`} style={{ width: `${Math.min(100, x.pct || 0)}%` }} /></div>
                    <p className="text-[10px] text-slate-500">{x.pct == null ? "—" : `${x.pct} %`}</p>
                  </div>); })}
              </div>
              {isManager && <ObjectiveEditor vendor={d.vendeur_id} periode={p} current={d.periodes[p]} onSaved={load} />}
            </div>
          ))}
        </Card>
      ))}
    </div>
  );
}

const CS = [["pct_ca", "% du CA"], ["pct_encaisse", "% du CA encaissé"], ["par_nouveau_client", "Montant par nouveau client"], ["bonus_objectif", "Bonus objectif mensuel atteint"], ["bonus_zero_ecart", "Bonus absence d'écart"]];

export function ViCommissions() {
  const { role } = useAuth();
  const canValidate = ["super_admin", "direction_generale", "direction_commerciale", "responsable", "comptable"].includes(role);
  const [mois, setMois] = useState(new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  const [cs, setCs] = useState(null);
  const load = () => api.get(`/itinerant/commissions?mois=${mois}`).then((r) => { setData(r.data); setCs(r.data.settings); });
  useEffect(() => { load(); }, [mois]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!data) return <Loader />;
  const validate = async (it) => {
    if (!window.confirm(`Valider ${money(it.total)} pour ${it.vendeur_name} ? Une charge « Commissions » sera créée.`)) return;
    if (await call(() => api.post("/itinerant/commissions/validate", { mois, vendeur_id: it.vendeur_id }), "Commission validée")) load();
  };
  return (
    <div data-testid="vi-commissions">
      <PageHeader title="Commissions" subtitle="État mensuel des commissions par vendeur"><Input type="month" value={mois} onChange={(e) => setMois(e.target.value)} className="w-44" data-testid="comm-month" /></PageHeader>
      {canValidate && <Card title="Paramètres de calcul">
        <div className="flex flex-wrap gap-3 items-end">
          {CS.map(([k, l]) => <label key={k} className="text-xs">{l}<Input type="number" className="mt-1 w-40" value={cs[k]} onChange={(e) => setCs({ ...cs, [k]: Number(e.target.value) })} data-testid={`comm-set-${k}`} /></label>)}
          <Button className="bg-brand-600 text-white" onClick={async () => { if (await call(() => api.put("/itinerant/commission-settings", cs), "Paramètres enregistrés")) load(); }} data-testid="comm-settings-save">Enregistrer</Button>
        </div>
      </Card>}
      <Card title={`État des commissions — ${mois}`} testid="comm-table">
        <MiniTable head={["Vendeur", "CA", "Encaissé", "Nouveaux clients", "Part CA", "Part encaissé", "Part nouveaux", "Bonus objectif", "Bonus 0 écart", "Total", "Statut", ""]}
          rows={data.items.map((it) => [it.vendeur_name, money(it.ca), money(it.encaisse), it.nouveaux_clients, money(it.part_ca), money(it.part_encaisse), money(it.part_nouveaux),
            money(it.bonus_objectif), money(it.bonus_zero_ecart), <b key="t" data-testid={`comm-total-${it.vendeur_id}`}>{money(it.total)}</b>,
            <span key="s"><StatusBadge value={it.statut} />{it.charge_numero && <span className="text-[10px] ml-1">{it.charge_numero}</span>}</span>,
            canValidate && it.statut !== "Validée" ? <Button key="v" size="sm" className="h-7 bg-brand-600 text-white" onClick={() => validate(it)} data-testid={`comm-validate-${it.vendeur_id}`}>Valider</Button> : ""])} />
      </Card>
    </div>
  );
}
